import React, {useState, useEffect} from 'react';
import {useRouter} from 'next/router';
import JobCard from '@components/Job/JobCard';
import RecruitmentService from '@services/recruitment.service';
import LoadingNavigation from '@components/common/Loading/LoadingNavigation';
import ApplicationForm from './ApplicationForm';
import FloatingApplyButton from './FloatingApplyButton';
import styles from './JobDetail.module.css';
import {getLocaleContent} from '@helper/locale-content.js';
import {safeHtml} from '@helper/sanitize-html';
/**
 * Component hiển thị chi tiết vị trí tuyển dụng
 * @param {Object} preloadedRecruitment - Dữ liệu vị trí tuyển dụng được load từ SSR
 * @param {Array} preloadedRelated - Danh sách vị trí tuyển dụng liên quan
 */
export default function JobDetail({preloadedRecruitment, preloadedRelated}) {
    const router = useRouter();
    const {id} = router.query;
    const {locale} = router;

    const [recruitment, setRecruitment] = useState(preloadedRecruitment || null);
    const [relatedRecruitments, setRelatedRecruitments] = useState(preloadedRelated || []);
    const [isLoading, setIsLoading] = useState(!preloadedRecruitment);
    const [isFormVisible, setIsFormVisible] = useState(false);

    // Cập nhật state khi preloadedRecruitment thay đổi
    useEffect(() => {
        if (preloadedRecruitment) {
            setRecruitment(preloadedRecruitment);
            setRelatedRecruitments(preloadedRelated || []);
            setIsLoading(false);
            // Scroll to top khi chuyển sang vị trí tuyển dụng khác
            window.scrollTo({top: 0, behavior: 'smooth'});
        }
    }, [preloadedRecruitment, preloadedRelated]);

    // Lấy chi tiết vị trí tuyển dụng (fallback khi không có SSR data)
    useEffect(() => {
        if (preloadedRecruitment) {
            return;
        }

        if (!locale || !id) return;

        const fetchRecruitmentDetail = async () => {
            setIsLoading(true);
            try {
                const recruitmentService = new RecruitmentService();
                const res = await recruitmentService.getRecruitmentById(id, {locale});

                if (res && res.recruitment) {
                    setRecruitment(res.recruitment);
                    setRelatedRecruitments(res.relatedRecruitments || []);
                }
            } catch (error) {
                console.error('Error fetching recruitment detail:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRecruitmentDetail();
    }, [locale, id, preloadedRecruitment]);

    // Định dạng ngày tháng theo chuẩn Việt Nam
    const formatDate = dateString => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('vi-VN');
    };

    // Chuyển đổi mã loại hình làm việc sang tên hiển thị
    const getEmploymentTypeName = (type, locale = 'vi') => {
        const types = {
            vi: {
                1: 'Toàn thời gian',
                2: 'Bán thời gian',
                3: 'Thực tập',
                4: 'Hợp đồng',
            },
            en: {
                1: 'Full-time',
                2: 'Part-time',
                3: 'Internship',
                4: 'Contract',
            },
        };

        const selectedTypes = types[locale] || types['vi'];
        return selectedTypes[type] || (locale === 'en' ? 'Full-time' : 'Toàn thời gian');
    };

    // Định dạng mức lương theo chuẩn tiền tệ Việt Nam
    const formatSalary = salary => {
        if (!salary || salary === '0') return 'Thỏa thuận';
        return salary;
    };

    // Chia sẻ thông tin tuyển dụng qua Web Share API hoặc clipboard
    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: recruitment.recruitment_name,
                text: recruitment.introduction || recruitment.job_description,
                url: window.location.href,
            });
        } else {
            navigator.clipboard.writeText(window.location.href);
            alert('Đã sao chép link vào clipboard!');
        }
    };

    // Chia sẻ lên Facebook
    const handleFacebookShare = () => {
        const url = window.location.href;
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        window.open(facebookUrl, '_blank', 'width=600,height=400');
    };

    // Chia sẻ lên X (Twitter)
    const handleXShare = () => {
        const url = window.location.href;
        const title = recruitment.recruitment_name || '';
        const text = `${title} - ${recruitment.introduction || ''}`;
        window.open(
            `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
            '_blank',
            'width=600,height=400',
        );
    };

    if (isLoading) {
        return (
            <section className={styles.jobDetailPage}>
                <LoadingNavigation size="large" color="#006940" />
            </section>
        );
    }

    if (!recruitment) {
        return (
            <section className={styles.jobDetailPage}>
                <div style={{textAlign: 'center', padding: '40px 20px'}}>
                    {getLocaleContent(locale, 'page.recruitment.no_news')}
                </div>
            </section>
        );
    }

    return (
        <section className={styles.jobDetailPage}>
            {/* Nội dung chính */}
            <div className={styles.jobDetailsContent}>
                <h1 className={styles.jobDetailTitle}>{recruitment.recruitment_name}</h1>

                {/* Thông tin tóm tắt công việc */}
                <div className={styles.jobSummaryCard}>
                    <div className={styles.jobSummaryItem}>
                        <img
                            src="/bw_image/ic_money.svg"
                            alt={getLocaleContent(locale, 'page.recruitment.salary_label')}
                        />
                        <div>
                            <p className={styles.label}>{getLocaleContent(locale, 'page.recruitment.salary_label')}</p>
                            <p className={`${styles.value} ${styles.highlight}`}>{formatSalary(recruitment.salary)}</p>
                        </div>
                    </div>

                    <div className={styles.jobSummaryItem}>
                        <img
                            src="/bw_image/ic_job_location.svg"
                            alt={getLocaleContent(locale, 'page.recruitment.location_label')}
                        />
                        <div>
                            <p className={styles.label}>
                                {getLocaleContent(locale, 'page.recruitment.location_label')}
                            </p>
                            <p className={styles.value}>
                                {recruitment.business_name || recruitment.location_name || 'Bệnh viện TNH'}
                            </p>
                        </div>
                    </div>

                    <div className={styles.jobSummaryItem}>
                        <img
                            src="/bw_image/ic_job_working_time.svg"
                            alt={getLocaleContent(locale, 'page.recruitment.working_time_label')}
                        />
                        <div>
                            <p className={styles.label}>
                                {getLocaleContent(locale, 'page.recruitment.working_time_label')}
                            </p>
                            <p className={styles.value}>{getEmploymentTypeName(recruitment.employment_type, locale)}</p>
                        </div>
                    </div>

                    <div className={styles.jobSummaryItem}>
                        <img
                            src="/bw_image/ic_job_frame.svg"
                            alt={getLocaleContent(locale, 'page.recruitment.quantity_label')}
                        />
                        <div>
                            <p className={styles.label}>
                                {getLocaleContent(locale, 'page.recruitment.quantity_label')}
                            </p>
                            <p className={styles.value}>{recruitment.quantity || '01'}</p>
                        </div>
                    </div>

                    <div className={styles.jobSummaryItem}>
                        <img
                            src="/bw_image/ic_job_calendar.svg"
                            alt={getLocaleContent(locale, 'page.recruitment.expired_label')}
                        />
                        <div>
                            <p className={styles.label}>{getLocaleContent(locale, 'page.recruitment.expired_label')}</p>
                            <p className={styles.value}>{formatDate(recruitment.expired_date)}</p>
                        </div>
                    </div>

                    <div className={`${styles.jobSummaryItem} ${styles.share}`}>
                        <img
                            src="/bw_image/ic_job_share.svg"
                            alt={getLocaleContent(locale, 'page.recruitment.share_label')}
                        />
                        <div>
                            <p className={styles.shareLabel} onClick={handleShare}>
                                {getLocaleContent(locale, 'page.recruitment.share_label')}
                            </p>
                            <div className={styles.socialIcons}>
                                <img src="/bw_image/i_fb_share.png" alt="Facebook" onClick={handleFacebookShare} />
                                <img src="/bw_image/i_x_share.png" alt="X (Twitter)" onClick={handleXShare} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.jobSectionContent}>
                    {recruitment.introduction && (
                        <>
                            <h2>{getLocaleContent(locale, 'page.booking-form.booking')}</h2>
                            <div dangerouslySetInnerHTML={safeHtml(recruitment.introduction)} />
                        </>
                    )}

                    {recruitment.job_description && (
                        <>
                            <h2>{getLocaleContent(locale, 'page.recruitment.job_description_title')}</h2>
                            <div dangerouslySetInnerHTML={safeHtml(recruitment.job_description)} />
                        </>
                    )}

                    {recruitment.requirements && (
                        <>
                            <h2>{getLocaleContent(locale, 'page.recruitment.requirements_title')}</h2>
                            <div dangerouslySetInnerHTML={safeHtml(recruitment.requirements)} />
                        </>
                    )}

                    {recruitment.benefits && (
                        <>
                            <h2>{getLocaleContent(locale, 'page.recruitment.benefits_title')}</h2>
                            <div dangerouslySetInnerHTML={safeHtml(recruitment.benefits)} />
                        </>
                    )}

                    {recruitment.request_profile && (
                        <>
                            <h2>{getLocaleContent(locale, 'page.recruitment.request_profile_title')}</h2>
                            <div dangerouslySetInnerHTML={safeHtml(recruitment.request_profile)} />
                        </>
                    )}

                    {recruitment.expired_date && (
                        <p className={styles.deadline}>
                            <strong>{getLocaleContent(locale, 'page.recruitment.application_period')}:</strong>{' '}
                            {formatDate(recruitment.expired_date)}.
                        </p>
                    )}
                </div>

                {relatedRecruitments.length > 0 && (
                    <div className={styles.relatedJobs}>
                        <h2>{getLocaleContent(locale, 'page.recruitment.position_label')}</h2>
                        <div className={styles.relatedGrid}>
                            {relatedRecruitments.map((job, idx) => (
                                <JobCard key={job.recruitment_id || idx} job={job} />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Form ứng tuyển cố định bên phải (chỉ hiện ở desktop) */}
            <aside className={styles.jobSidebar}>
                <ApplicationForm
                    key={recruitment?.recruitment_id}
                    recruitment={recruitment}
                    isVisible={true}
                    onClose={() => {}}
                    isModal={false}
                />
            </aside>

            {/* Nút ứng tuyển nổi (hiển thị ở mobile/tablet khi form đóng) */}
            <FloatingApplyButton
                isVisible={!isFormVisible}
                onClick={() => {
                    setTimeout(() => setIsFormVisible(true), 10);
                }}
            />

            {/* Form ứng tuyển dạng modal (hiển thị ở mobile/tablet) */}
            <ApplicationForm
                key={`modal-${recruitment?.recruitment_id}`}
                recruitment={recruitment}
                isVisible={isFormVisible}
                onClose={() => setIsFormVisible(false)}
                isModal={true}
            />
        </section>
    );
}
